import { registerComponent, asyncRequest, debounce, loading } from "helpers";
import WYATTElement from "wyatt";

registerComponent(
  "cart-items",
  class CartItems extends WYATTElement {
    SECTIONS_TO_RENDER = [
      {
        name: "cart-count",
        liveRegion: "cart-count",
      },
      {
        name: "cart",
        liveRegion: "cart-items",
      },
      {
        name: "cart",
        liveRegion: "cart-totals",
      },
      {
        name: "cart",
        liveRegion: "cart-checkout",
      },
      {
        name: "cart",
        liveRegion: "cart-recommendations",
      },
    ];

    constructor() {
      super();

      this.removeButtons = this.querySelectorAll("[data-remove-item-button]");
      this.form = this.querySelector("form");
      this.currentItemCount = Array.from(
        this.querySelectorAll('[name="updates[]"]'),
      ).reduce(
        (total, quantityInput) => total + parseInt(quantityInput.value, 10),
        0,
      );

      this.removeButtons.forEach((button) => {
        button.addEventListener("click", this.remove.bind(this));
      });

      const debouncedChangeHandler = debounce((event) => {
        this.handleChange(event);
      }, 300);

      this.form?.addEventListener("change", debouncedChangeHandler);
    }

    remove(event) {
      const line = event.currentTarget.dataset.removeItemButton;

      event.preventDefault();

      this.updateQuantity({ line, quantity: 0 });
    }

    handleChange(event) {
      const { target } = event;
      const key = target.name.replace(/^updates\[(.+)\]$/, "$1");
      const quantity = parseInt(target.value, 10);
      const syncKeys = target.dataset.personalizationSyncKeys
        ? target.dataset.personalizationSyncKeys.split(",").filter(Boolean)
        : [];

      if (syncKeys.length > 0) {
        const updates = { [key]: quantity };

        syncKeys.forEach((syncKey) => {
          updates[syncKey] = quantity;
        });

        this.updateQuantities({ updates });
      } else {
        this.updateQuantity({ id: key, quantity });
      }
    }

    getSectionNames() {
      return [
        ...new Set(this.SECTIONS_TO_RENDER.map((section) => section.name)),
      ];
    }

    /**
     * Changes item quantities. Either id or line is required
     * @param {object} params - Change line item params
     */
    async updateQuantity(params) {
      const body = {
        ...params,
        sections: this.getSectionNames(),
        sections_url: window.location.pathname,
      };
      const options = {
        ...WYATT.config.fetch.post,
        ...{ body: JSON.stringify(body) },
      };

      loading.add(this);

      const response = await asyncRequest(
        WYATT.routes.cart_change_url,
        options,
        {
          messageOptions: {
            minDelay: 6000,
            appendPoint:
              '[data-mini-cart][active="true"] [data-dialog-messages], #page-messages',
          },
        },
      );

      if (response) {
        await this.handleSuccessfulUpdate(response);
      }

      loading.remove(this);
    }

    /**
     * Changes quantities for multiple line items at once, keyed by line item key.
     * @param {object} updates - Update line items params
     */
    async updateQuantities({ updates }) {
      const body = {
        updates,
        sections: this.getSectionNames(),
        sections_url: window.location.pathname,
      };
      const options = {
        ...WYATT.config.fetch.post,
        ...{ body: JSON.stringify(body) },
      };

      loading.add(this);

      const response = await asyncRequest(
        WYATT.routes.cart_update_url,
        options,
        {
          messageOptions: {
            minDelay: 6000,
            appendPoint:
              '[data-mini-cart][active="true"] [data-dialog-messages], #page-messages',
          },
        },
      );

      if (response) {
        await this.handleSuccessfulUpdate(response);
      }

      loading.remove(this);
    }

    /**
     * Bundle setup-charge line items are added as ordinary top-level cart
     * items tied to their parent via the `_parent_item` property (bundled
     * products can't use Shopify's native nested line items). If the
     * parent is no longer in the cart, its setup charge is orphaned and
     * should be removed.
     * @param {object[]} items - The cart's line items
     * @returns {object} Update params keyed by line item key
     */
    getChildItemUpdates(items) {
      const parentVariantIds = new Set(
        items.map((item) => String(item.variant_id)),
      );
      const updates = {};

      items.forEach((item) => {
        // eslint-disable-next-line no-underscore-dangle
        const parentVariantId = item.properties?._parent_item;

        if (!parentVariantId) return;

        if (!parentVariantIds.has(String(parentVariantId))) {
          updates[item.key] = 0;
        }
      });

      return updates;
    }

    /**
     * Applies corrective updates (orphan removal) for child
     * line items and returns the resulting cart state.
     * @param {object} updates - Update line items params
     * @returns {object|null} The parsed cart state, or null on failure
     */
    async applyChildItemUpdates(updates) {
      const body = {
        updates,
        sections: this.getSectionNames(),
        sections_url: window.location.pathname,
      };
      const options = {
        ...WYATT.config.fetch.post,
        ...{ body: JSON.stringify(body) },
      };

      const response = await asyncRequest(
        WYATT.routes.cart_update_url,
        options,
      );

      return response ? JSON.parse(response) : null;
    }

    async handleSuccessfulUpdate(state) {
      let parsedState = JSON.parse(state);
      const childUpdates = this.getChildItemUpdates(parsedState.items);

      if (Object.keys(childUpdates).length > 0) {
        parsedState =
          (await this.applyChildItemUpdates(childUpdates)) || parsedState;
      }

      this.publish(
        WYATT.PubSub.events.updateDOM,
        this.SECTIONS_TO_RENDER.map(({ liveRegion, name }) => ({
          liveRegion,
          html: parsedState.sections[name],
        })),
      );
    }
  },
);
